/**
 * ForgotPasswordPage Component
 * Mobile-friendly forgot password page matching the login screen design.
 *
 * Two reset modes (per backend contract):
 *   - "username": send { username }
 *   - "email":    send { email, agencyId }   ← email alone is no longer accepted
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  EnvelopeIcon,
  UserIcon,
  BuildingOfficeIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { oathForgotPassword } from '../../services/oathAuthService';
import { SimpleSpinner } from '../ui/Spinner';

const MODE_USERNAME = 'username';
const MODE_EMAIL = 'email';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [mode, setMode] = useState(MODE_USERNAME);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [agencyId, setAgencyId] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const switchMode = (next) => {
    if (isSubmitting || mode === next) return;
    setMode(next);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let payload;
    if (mode === MODE_USERNAME) {
      const u = username.trim();
      if (!u) {
        setError('Please enter your username');
        return;
      }
      payload = { username: u };
    } else {
      const em = email.trim();
      const ag = agencyId.trim();
      if (!em) {
        setError('Please enter your email address');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(em)) {
        setError('Please enter a valid email address');
        return;
      }
      if (!ag) {
        setError('Please enter your Agency ID');
        return;
      }
      if (!/^\d+$/.test(ag)) {
        setError('Agency ID must be a number');
        return;
      }
      payload = { email: em, agencyId: parseInt(ag, 10) };
    }

    setIsSubmitting(true);
    await oathForgotPassword(payload);
    setIsSubmitting(false);

    // Always show success to prevent enumeration of usernames / email+agency pairs
    setIsSuccess(true);
  };

  const handleBackToLogin = () => {
    navigate('/');
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>

        {/* Mobile Header with Logo */}
        <div className="py-5 px-4 bg-white border-b border-gray-100 relative z-10">
          <div className="flex flex-col items-center gap-1">
            <img
              src="/bankers-surety-logo.png"
              alt="Bankers Surety"
              className="h-9"
            />
            <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
          <div className="w-full max-w-sm sm:max-w-md">
            {/* Success Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
              <div className="h-1 bg-gradient-to-r from-green-400 via-green-500 to-green-400"></div>
              <div className="p-6 sm:p-8 lg:p-10 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircleIcon className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Check Your Email
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  If an account matches what you entered, we've sent a password reset link.
                  The link will expire in 1 hour.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <button
                  onClick={handleBackToLogin}
                  className="w-full py-3.5 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-5 px-4 text-center relative z-10">
          <p className="text-xs text-primary-200/80">
            © {new Date().getFullYear()} Bankers Surety. All rights reserved.
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primary-600 via-primary to-primary-700 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-900/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
      </div>

      {/* Mobile Header with Logo */}
      <div className="py-5 px-4 bg-white border-b border-gray-100 relative z-10">
        <div className="flex flex-col items-center gap-1">
          <img
            src="/bankers-surety-logo.png"
            alt="Bankers Surety"
            className="h-9"
          />
          <span className="text-gray-500 text-xs font-medium">Agency Portal</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 relative z-10">
        <div className="w-full max-w-sm sm:max-w-md">
          {/* Branding */}
          <div className="text-center mb-8 sm:mb-10">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
              Forgot Password
            </h1>
            <p className="text-sm sm:text-base text-primary-100 mt-2">
              We'll send you a reset link
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/20">
            <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400"></div>
            <div className="p-6 sm:p-8 lg:p-10">
              {/* Mode toggle */}
              <div
                role="tablist"
                aria-label="Reset method"
                className="flex p-1 mb-5 bg-gray-100 rounded-xl"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === MODE_USERNAME}
                  onClick={() => switchMode(MODE_USERNAME)}
                  disabled={isSubmitting}
                  className={`flex-1 py-2 px-3 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    mode === MODE_USERNAME
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Username
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === MODE_EMAIL}
                  onClick={() => switchMode(MODE_EMAIL)}
                  disabled={isSubmitting}
                  className={`flex-1 py-2 px-3 text-sm font-semibold rounded-lg transition-all duration-200 ${
                    mode === MODE_EMAIL
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Email + Agency ID
                </button>
              </div>

              {/* Error Alert */}
              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-700 text-center">{error}</p>
                </div>
              )}

              <p className="text-sm text-gray-600 text-center mb-6">
                {mode === MODE_USERNAME
                  ? "Enter your username and we'll send a reset link to the email on file."
                  : "Enter your email and Agency ID and we'll send you a link to reset your password."}
              </p>

              <form onSubmit={handleSubmit} noValidate>
                {mode === MODE_USERNAME ? (
                  /* Username field */
                  <div className="mb-6">
                    <label
                      htmlFor="username"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Username
                    </label>
                    <div className="relative">
                      <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="username"
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        disabled={isSubmitting}
                        autoComplete="username"
                        autoFocus
                        placeholder="Enter your username"
                        className="w-full pl-12 pr-4 py-3.5 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                      />
                    </div>
                  </div>
                ) : (
                  /* Email + Agency ID fields */
                  <>
                    <div className="mb-4">
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                          <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          disabled={isSubmitting}
                          autoComplete="email"
                          autoFocus
                          placeholder="Enter your email"
                          className="w-full pl-12 pr-4 py-3.5 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                    <div className="mb-6">
                      <label
                        htmlFor="agencyId"
                        className="block text-sm font-semibold text-gray-700 mb-2"
                      >
                        Agency ID
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                          <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="agencyId"
                          type="text"
                          inputMode="numeric"
                          value={agencyId}
                          onChange={(e) => setAgencyId(e.target.value.replace(/\D/g, ''))}
                          disabled={isSubmitting}
                          autoComplete="off"
                          placeholder="From your registration"
                          className="w-full pl-12 pr-4 py-3.5 text-base sm:text-sm border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Buttons row */}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="flex-1 py-3 px-4 text-gray-600 hover:text-primary font-medium rounded-xl border border-gray-300 hover:border-primary/30 transition-colors flex items-center justify-center gap-2"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Sign In
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-[2] py-3 px-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center shadow-lg shadow-primary/30 hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    {isSubmitting ? (
                      <>
                        <SimpleSpinner size="sm" className="text-white mr-2" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="text-sm text-primary-100">Need assistance?</span>
              <a
                href="tel:877-778-5245"
                className="text-sm text-white font-semibold hover:text-primary-50 transition-colors"
              >
                877-778-5245
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-5 px-4 text-center relative z-10">
        <p className="text-xs text-primary-200/80">
          © {new Date().getFullYear()} Bankers Surety. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default ForgotPasswordPage;
